
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import expandIcon from "../../../../assets/icons/expandDown.svg";
import shrinkIcon from "../../../../assets/icons/expandUp.svg";
import TextFieldInput from "../../../../components/TextFieldInput";
import { getCurrentFormPhase, formatNumberIndian } from "../../../../utilis/common-funtions";
import { useSelector, useDispatch } from "react-redux";
import {
  createRazorpayOrder,
  verifyVoucherPayment,
  getVoucherById,
  setUnitPrefEditFromThankYou,
} from "../../voucher-form.slice";
import { useParams } from "react-router-dom";
import { useRazorpayPayment } from "../../components/payments/useRazorpayPayment";
import { useFormik } from "formik";
import { GATEWAY, PAYMENT_ENTITY_TYPE } from "../../../../config/constant";
import * as Yup from "yup";
import { useEasebuzzPayment } from "../payments/useEasebuzzPayment";
import PaymentFailedModal from "../../../new-booking-form/Individual-components/PaymentFailedModal";

function extractPaymentDetails(data) {
  // Fallback to 0 if not provided
  const amountPayable = Number(data?.amountPayable) || 0;

  // Prefer `totalAmountPaid` if exists, else sum from transactions
  let amountPaid = Number(data?.totalAmountPaid) || 0;

  // Ensure non-negative due
  let amountDue = amountPayable - amountPaid;
  if (amountDue < 0) amountDue = 0;

  return {
    amountPayable,
    amountPaid,
    amountDue,
  };
}

const PaymentStatus = () => {
  const paymentStatusformik = useFormik({
    initialValues: {
      amount: "",
    },
    validationSchema: Yup.object({
      amount: Yup.string().required("Please enter amount in field to Pay")
    })
  });

  const [open, setOpen] = useState(true);
  const [paymentAmount, setPaymentAmount] = useState("");
  const { applicantData, eoiCampaignDetails } = useSelector((state) => state.voucherForm);
  const dispatch = useDispatch();
  const { voucherId } = useParams();
  const {
    initiatePayment,
    paymentResponse,
    loading: razorpayCheckoutLoading,
  } = useRazorpayPayment();
  const {
    initiateEasebuzzPayment,
    loading: easebuzzCheckoutLoading,
    paymentResponse: easebuzzPaymentResponse,
  } = useEasebuzzPayment();
  const [openPaymentFailedModal, setOpenPaymentFailedModal] = useState(false);
  const [failedAmount, setFailedAmount] = useState("");
  const [failedGatewayForRetry, setFailedGatewayForRetry] = useState(null);

  const availableGateways = eoiCampaignDetails?.availableGateways ?? [GATEWAY.EASEBUZZ, GATEWAY.RAZORPAY];
  const hasOnlyRazorpay =
    Array.isArray(availableGateways) &&
    availableGateways.length === 1 &&
    availableGateways.includes(GATEWAY.RAZORPAY);
  const hasOnlyEasebuzz =
    Array.isArray(availableGateways) &&
    availableGateways.length === 1 &&
    availableGateways.includes(GATEWAY.EASEBUZZ);


  const [verifying, setVerifying] = useState(false);

  const handleEasebuzzPay = async (amount) => {
    await paymentStatusformik.validateForm();
    paymentStatusformik.setTouched({ amount: true });
    if (!amount) return
    try {
      const orderRes = await dispatch(
        createRazorpayOrder({
          amount,
          entityType: PAYMENT_ENTITY_TYPE.VOUCHER,
          entityId: applicantData?.id?.toString(),
          projectId: applicantData?.campaignId,
          gateway: GATEWAY?.EASEBUZZ,

          redirectUrl: globalThis.location.href,
          notes: {
            productInfo: applicantData?.uniqueReferenceId,
            voucherId: voucherId,
            voucherAmount: Number(PaymentObject?.amountPayable),
            guest: {
              name: `${applicantData?.applicant1?.personalDetails?.firstName} ${applicantData?.applicant1?.personalDetails?.lastName}`.trim(),
              email: applicantData?.applicant1?.personalDetails?.emailAddress,
              phone: applicantData?.applicant1?.personalDetails?.contactNumber,
            },
          },
        })
      ).unwrap();

      const orderAccessKey = orderRes.accessKey;

      if (!orderAccessKey) {
        throw new Error(
          "Backend did not return access_key. Check createRazorpayOrder response: " +
          JSON.stringify(orderRes)
        );
      }

      await initiateEasebuzzPayment({
        orderAccessKey,
        easebuzzKey: orderRes?.easebuzzKey,
        originalAmount: amount,
        onModalClose: (amt) => {
          setFailedAmount(amt);
          setOpenPaymentFailedModal(true);
          setFailedGatewayForRetry(hasOnlyEasebuzz ? GATEWAY?.EASEBUZZ : null);
        },
      });
    } catch (err) {
      setFailedAmount(amount);
      setOpenPaymentFailedModal(true);
      setFailedGatewayForRetry(hasOnlyEasebuzz ? GATEWAY?.EASEBUZZ : null);
      console.error("Failed to initiate payment:", err);
    }
  };


  const PaymentObject = extractPaymentDetails(applicantData?.paymentDetails);

  useEffect(() => {
    if (!paymentResponse) return;

    let attempts = 0;
    let isVerified = false;
    setFailedAmount('');

    const verifyPayment = async () => {
      if (attempts === 0) setVerifying(true); // start loading
      attempts++;

      const res = await dispatch(
        verifyVoucherPayment({
          orderId: paymentResponse?.razorpay_order_id,
          paymentId: paymentResponse?.razorpay_payment_id,
          signature: paymentResponse?.razorpay_signature,
          gateway: GATEWAY?.RAZORPAY,
          clientResponse: JSON.stringify(paymentResponse)
        })
      );

      if (res?.payload?.status === "success") {
        isVerified = true;
        setVerifying(false);
        dispatch(setUnitPrefEditFromThankYou(false));
        dispatch(getVoucherById(`/${voucherId}`));
        setPaymentAmount("");
        paymentStatusformik?.resetForm();
      } else if (attempts < 3 && !isVerified) {
        // retry after short delay
        setTimeout(verifyPayment, 2000);
      } else {
        setVerifying(false);
        setFailedAmount(paymentStatusformik?.values?.amount);
        setOpenPaymentFailedModal(true);
        setFailedGatewayForRetry(hasOnlyRazorpay ? GATEWAY?.RAZORPAY : null);
      }
    };

    verifyPayment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentResponse]);

  const handleRazorPay = async (amount) => {
    await paymentStatusformik.validateForm();
    paymentStatusformik.setTouched({ amount: true });
    if (!amount) return;
    try {
      const orderRes = await dispatch(
        createRazorpayOrder({
          amount: amount,
          entityType: PAYMENT_ENTITY_TYPE.VOUCHER,
          entityId: applicantData?.id?.toString(),
          projectId: applicantData?.campaignId,
          gateway: GATEWAY?.RAZORPAY,
          notes: {
            voucherId: voucherId,
            voucherAmount: Number(PaymentObject?.amountPayable),
            productInfo: applicantData?.uniqueReferenceId,
            guest: {
              name: `${applicantData?.applicant1?.personalDetails?.firstName} ${applicantData?.applicant1?.personalDetails?.lastName}`.trim(),
              email: applicantData?.applicant1?.personalDetails?.emailAddress,
              phone: applicantData?.applicant1?.personalDetails?.contactNumber,
            },
          },
        })
      ).unwrap();
      initiatePayment({
        amount: orderRes?.razorpayOrder?.amount,
        currency: orderRes?.razorpayOrder?.currency,
        orderId: orderRes?.razorpayOrder?.id,
        name: "Puravankara",
        description: "Payment",
        prefill: {
          name: orderRes?.razorpayOrder?.notes?.guest?.name,
          email: orderRes?.razorpayOrder?.notes?.guest?.email,
          contact: orderRes?.razorpayOrder?.notes?.guest?.phone,
        },
        razorpayKey: orderRes?.razorpayKey,
        originalAmount: amount,
        onFailure: (amt) => {
          setFailedAmount(amt);
          setOpenPaymentFailedModal(true);
          setFailedGatewayForRetry(hasOnlyRazorpay ? GATEWAY?.RAZORPAY : null);
        },
      });
    } catch (err) {
      console.error("Payment failed:", err);
      setFailedAmount(amount);
      setOpenPaymentFailedModal(true);
      setFailedGatewayForRetry(hasOnlyRazorpay ? GATEWAY?.RAZORPAY : null);
    }
  };
  useEffect(() => {
    if (!easebuzzPaymentResponse) return;

    if (easebuzzPaymentResponse && easebuzzPaymentResponse?.status === "failure") {
      setFailedAmount(easebuzzPaymentResponse?.amount);
      setOpenPaymentFailedModal(true);
      setFailedGatewayForRetry(hasOnlyEasebuzz ? GATEWAY?.EASEBUZZ : null);
      return;
    }
    let attempts = 0;
    let isVerified = false;
    setFailedAmount('');

    const verifyPayment = async () => {
      if (attempts === 0) setVerifying(true); // start loading
      attempts++;

      const res = await dispatch(
        verifyVoucherPayment({
          orderId: easebuzzPaymentResponse?.txnid,
          paymentId: easebuzzPaymentResponse?.easepayid,
          signature: easebuzzPaymentResponse?.hash,
          gateway: GATEWAY?.EASEBUZZ,
          clientResponse: JSON.stringify(easebuzzPaymentResponse)
        })
      );

      if (res?.payload?.status === "success") {
        isVerified = true;
        setVerifying(false);
        dispatch(setUnitPrefEditFromThankYou(false));
        dispatch(getVoucherById(`/${voucherId}`));
        setPaymentAmount("");
        paymentStatusformik?.resetForm();
      } else if (attempts < 3 && !isVerified) {
        // retry after short delay
        setTimeout(verifyPayment, 2000);
      } else {
        setVerifying(false);
        setFailedAmount(paymentStatusformik?.values?.amount);
        setOpenPaymentFailedModal(true);
        setFailedGatewayForRetry(hasOnlyEasebuzz ? GATEWAY?.EASEBUZZ : null);
      }
    };
    if (easebuzzPaymentResponse && easebuzzPaymentResponse?.status === "success") {
      verifyPayment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [easebuzzPaymentResponse]);

  const handleProceedGateway = (selectedGateway) => {
    if (selectedGateway === GATEWAY?.RAZORPAY) {
      handleRazorPay(failedAmount);
      setFailedAmount("");
    }
    if (selectedGateway === GATEWAY?.EASEBUZZ) {
      handleEasebuzzPay(failedAmount);
      setFailedAmount("");
    }
    setOpenPaymentFailedModal(false);
    setFailedGatewayForRetry(null);
  };

  const handleClosePaymentFailedModal = () => {
    setOpenPaymentFailedModal(false);
    setFailedGatewayForRetry(null);
  };

  return (
    <Box
      sx={{ border: "1px solid rgba(26, 64, 125, 0.1)", borderRadius: "16px" }}
      p={3}
      mb={3}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: "pointer",
        }}
        onClick={() => setOpen(!open)}
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Payment Status
        </Typography>
        <img src={open ? shrinkIcon : expandIcon} alt="toggle-icon" />
      </Box>

      <Collapse in={open}>
        <Box mt={4}>
          <Grid container spacing={1} alignItems={"center"}>
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {getCurrentFormPhase(applicantData?.formPhase)} Amount
              </Typography>
            </Grid>
            <Grid item xs={6} textAlign="right">
              <Typography
                sx={{
                  fontSize: "20px",
                  fontWeight: 700,
                  color: "rgba(26, 64, 125, 1)",
                }}
              >
                {PaymentObject?.amountPayable
                  ? `₹${formatNumberIndian(PaymentObject.amountPayable)}`
                  : "₹0"}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Amount Paid
              </Typography>
            </Grid>
            <Grid item xs={6} textAlign="right">
              <Typography
                sx={{
                  fontSize: "20px",
                  fontWeight: 700,
                  color: "rgba(26, 64, 125, 1)",
                }}
              >
                {PaymentObject?.amountPaid
                  ? `₹${formatNumberIndian(PaymentObject.amountPaid)}`
                  : "₹0"}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 1.5 }} />

          <Grid container mt={2} alignItems={"center"}>

            <Grid item xs={6}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Balance Due
              </Typography>
            </Grid>
            <Grid item xs={6} textAlign="right">
              <Typography
                sx={{
                  fontSize: "20px",
                  fontWeight: 700,
                  color: "rgba(26, 64, 125, 1)",
                }}
              >
                {PaymentObject?.amountDue
                  ? `₹${formatNumberIndian(PaymentObject.amountDue)}`
                  : "₹0"}
              </Typography>
            </Grid>
          </Grid>

          <Grid
            container
            spacing={2}
            mt={2}
            alignItems="center"
            justifyContent="center"
            sx={{ flexWrap: "wrap" }}
          >
            {/* Input Field */}

            <Grid item xs={12}>
              <TextFieldInput
                field={{
                  placeholder: "Enter amount",
                  name: "amount",
                  required: false,
                  maxLength: 15,
                  handleOnChange: (e) => {
                    const value = e.target.value || "";
                    const numericValue = value.replaceAll(/\D+/g, "");

                    paymentStatusformik.setFieldValue("amount", numericValue);
                    setPaymentAmount(numericValue); // ✅ FIX
                  },
                  readonly: false,
                }}
                formik={paymentStatusformik}
              />
            </Grid>


            {/* Pay Button */}

            <Grid item xs={12}>
              <Button
                fullWidth
                onClick={() =>
                  hasOnlyRazorpay
                    ? handleRazorPay(paymentAmount)
                    : handleEasebuzzPay(paymentAmount)
                }
                variant="contained"
                disabled={
                  razorpayCheckoutLoading ||
                  easebuzzCheckoutLoading ||
                  verifying
                }
                sx={{
                  borderRadius: "8px",
                  fontWeight: 600,
                  textTransform: "none",
                  backgroundColor: "rgba(26, 64, 125, 1)",
                  fontSize: "14px",
                  "&:hover": {
                    backgroundColor: "rgba(26, 64, 125, 1)",
                  },
                  height: "40px",
                  minHeight: 40,
                }}
              >
                {razorpayCheckoutLoading ||
                  easebuzzCheckoutLoading ||
                  verifying ? (
                  <CircularProgress size={22} sx={{ color: "#fff" }} />
                ) : (
                  "Pay Now"
                )}
              </Button>
            </Grid>

          </Grid>

        </Box>
      </Collapse>
      <PaymentFailedModal
        open={openPaymentFailedModal}
        onClose={handleClosePaymentFailedModal}
        onProceed={handleProceedGateway}
        singleGatewayMode={failedGatewayForRetry != null}
        retryGateway={failedGatewayForRetry}
      />
    </Box>
  );
};

export default PaymentStatus;